import React, { useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { Button, Checkbox, Col, Form, Input, InputNumber, Row, Select, Spin } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import Title from 'antd/es/typography/Title';
import { checkIntKrQuantYear, getKrQuantCheckInYear } from 'app/entities/checkIn/checkIn-year.reducer';
import { reset } from 'app/entities/checkIn/checkIn.reducer';
import ObjectiveUpdate from 'app/entities/objective/form/objective-update';
import { KrTypes } from 'app/shared/model/enum/kr.enum';
import { QuantitativeEvaluationType } from 'app/shared/model/enum/quantitativeEvaluation.enum';
import { StatusType } from 'app/shared/model/enum/status.enum';
import { TrendingTypes } from 'app/shared/model/enum/trending.enum';
import { formatNumber, parseNumber } from 'app/shared/util/help';
import { displayPercent, formatPoint, percent } from 'app/shared/util/percent';
import { filterOption } from 'app/shared/util/select-filter';
import * as _ from 'lodash';
import KRUpdate from '../../kr-update';

type IReportQuantitativeResult = {
  handleClose: (isReload?: boolean) => void;
  krCheckIn?: any;
  handleNextData: () => void;
  setUpdateSuccess: (isReload?: boolean) => void;
  isReadOnly?: boolean;
};

export const ReportQuantitativeResult = ({ handleClose, krCheckIn, handleNextData, setUpdateSuccess, isReadOnly }: IReportQuantitativeResult) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const filters = useAppSelector(state => state.filters.orgCheckIn);
  const workStatusList = useAppSelector(state => state.common.workStatus);
  const entity = useAppSelector(state => state.checkInYear.entity);

  const [isOpenModalFormKr, setIsOpenModalFormKr] = useState(false);
  const [isOpenModalFormObjective, setIsOpenModalFormObjective] = useState(false);
  const [isUpdating, setUpdating] = useState(false);

  // watch values form
  const workStatusMonthValues = Form.useWatch('workStatusMonth', form);
  const workStatusQuarterValues = Form.useWatch('workStatusQuarter', form);
  const workStatusYearValues = Form.useWatch('workStatusYear', form);

  const metricValueMonthValues = Form.useWatch('metricValueMonth', form);
  const metricValueQuarterValues = Form.useWatch('metricValueQuarter', form);
  const metricValueYearValues = Form.useWatch('metricValueYear', form);
  const actualResultMonthValues = Form.useWatch('actualResultMonth', form); // Chốt thực hiện tháng trước

  useEffect(() => {
    if (krCheckIn && filters) {
      innitData();
    }
  }, [krCheckIn]);

  useEffect(() => {
    if (entity) {
      form.setFieldsValue({ reportScheduleName: entity?.reportScheduleName })
      if (entity?.krMonth) {
        form.setFieldValue('workStatusMonth', entity?.krMonth?.workStatus)
        form.setFieldValue('metricValueMonth', entity?.krMonth?.result)
        form.setFieldValue('unFinishNoteMonth', entity?.krMonth?.unFinishNote)
        form.setFieldValue('estimatePointMonth', formatPoint(entity?.krMonth?.estimatePoint))
      }
      if (entity?.krPrevMonth) {
        form.setFieldValue('actualResultMonth', entity?.krPrevMonth?.actualResult)
      }
      if (entity?.krQuarter) {
        form.setFieldValue('workStatusQuarter', entity?.krQuarter?.workStatus)
        form.setFieldValue('metricValueQuarter', entity?.krQuarter?.result)
        form.setFieldValue('unFinishNoteQuarter', entity?.krQuarter?.unFinishNote)
        form.setFieldValue('estimatePointQuarter', formatPoint(entity?.krQuarter?.estimatePoint))
      }
      if (entity?.krYear) {
        form.setFieldValue('workStatusYear', entity?.krYear?.workStatus)
        form.setFieldValue('metricValueYear', entity?.krYear?.result)
        form.setFieldValue('unFinishNoteYear', entity?.krYear?.unFinishNote)
        form.setFieldValue('issue', entity?.krYear?.issue)
        form.setFieldValue('note', entity?.krYear?.note)
        form.setFieldValue('solution', entity?.krYear?.solution)
        form.setFieldValue('estimatePointYear', formatPoint(entity?.krYear?.estimatePoint))
      }
    }
  }, [entity]);

  useEffect(() => {
    if (krCheckIn?.krType === KrTypes.AccumulateQuantitative) {
      // lũy kế thực hiện quý
      const valueQuarter = (entity?.krQuarter?.result ?? 0) - (entity?.krPrevMonth?.result ?? 0) + parseFloat(actualResultMonthValues ?? 0) + (metricValueMonthValues ?? 0);
      if (_.isInteger(valueQuarter)) form.setFieldValue('metricValueQuarter', valueQuarter);
      else form.setFieldValue('metricValueQuarter', parseFloat(valueQuarter?.toFixed(2)));
    }
    if (entity?.krMonth?.evaluationMethodQuantitative === QuantitativeEvaluationType.Point) {
      // % thực hiện tháng tính đến nửa tháng
      const resultMonth = displayPercent(
        calculatorPercentByTrending(
          entity?.krMonth?.metricType,
          metricValueMonthValues,
          entity?.krMonth?.metricValue,
        ),
      );
      if (_.isInteger(resultMonth)) form.setFieldValue('estimatePointMonth', resultMonth);
      else form.setFieldValue('estimatePointMonth', parseFloat(resultMonth.toFixed(2)));
    }
  }, [metricValueMonthValues, actualResultMonthValues])

  useEffect(() => {
    if (krCheckIn?.krType === KrTypes.AccumulateQuantitative && metricValueQuarterValues !== null) {
      // lũy kế thực hiện năm
      const valueYear = (metricValueQuarterValues ?? 0) + (entity?.krYear?.result ?? 0);

      if (_.isInteger(valueYear)) form.setFieldValue('metricValueYear', valueYear);
      else form.setFieldValue('metricValueYear', parseFloat(valueYear?.toFixed(2)));
    }

    if (entity?.krQuarter?.evaluationMethodQuantitative === QuantitativeEvaluationType.Point) {
      // % thực hiện quý tính đến nửa tháng
      const percentResultQuarter = displayPercent(
        calculatorPercentByTrending(
          entity?.krQuarter?.metricType,
          metricValueQuarterValues,
          entity?.krQuarter?.metricValue,
        ),
      );
      if (_.isInteger(percentResultQuarter)) form.setFieldValue('estimatePointQuarter', percentResultQuarter);
      else form.setFieldValue('estimatePointQuarter', parseFloat(percentResultQuarter.toFixed(2)));
    }
  }, [metricValueQuarterValues])

  useEffect(() => {
    if (entity?.krYear?.evaluationMethodQuantitative === QuantitativeEvaluationType.Point && metricValueYearValues !== null) {
      // % thực hiện lũy kế năm tính đến nửa tháng
      const percentResultYear = displayPercent(
        calculatorPercentByTrending(
          entity?.krYear?.metricType,
          metricValueYearValues,
          entity?.krYear?.metricValue,
        ),
      );
      if (_.isInteger(percentResultYear)) form.setFieldValue('estimatePointYear', percentResultYear);
      else form.setFieldValue('estimatePointYear', parseFloat(percentResultYear.toFixed(2)));
    }
  }, [metricValueYearValues])

  const onClose = (isReload = false) => {
    form.resetFields();
    handleClose(isReload);
  }

  const innitData = () => {
    if (filters.reportScheduleId && krCheckIn.id)
      dispatch(getKrQuantCheckInYear({ krId: krCheckIn?.id, reportScheduleId: filters?.reportScheduleId }));
  };

  const calculatorPercentByTrending = (calculation, numerator, denominator): number => {
    if (calculation === TrendingTypes.Above) {
      if (denominator > 0) return (numerator ?? 0) / denominator;
      return 0;
    } else {
      if (numerator > 0) return denominator / numerator;
      return 0;
    }
  };

  const MapperPayload = (values: any) => {
    return {
      krId: krCheckIn?.id,
      krYear: {
        workStatus: values.workStatusYear,
        estimatePoint: percent(values.estimatePointYear),
        unFinishNote: values.unFinishNoteYear,
        issue: values.issue,
        solution: values.solution,
        note: values.note,
        result: values.metricValueYear
      },
      krQuarter: {
        workStatus: values.workStatusQuarter,
        unFinishNote: values.unFinishNoteQuarter,
        estimatePoint: percent(values.estimatePointQuarter),
        result: values.metricValueQuarter
      },
      krMonth: {
        workStatus: values.workStatusMonth,
        unFinishNote: values.unFinishNoteMonth,
        estimatePoint: percent(values.estimatePointMonth),
        result: values.metricValueMonth
      },
      krPrevMonth: {
        actualResult: values.actualResultMonth,
      }
    }
  };

  const handleSubmit = values => {
    const payload = MapperPayload(values);
    setUpdating(true);
    dispatch(checkIntKrQuantYear(payload))
      .then(response => {
        if (response && response.payload) {
          const result = (response.payload as any).data
          if (!result) return;
          dispatch(reset());
          form.resetFields();
          onClose(true);
        }
      })
      .catch(() => {
        return;
      })
      .finally(() => {
        setUpdating(false);
      });

  };

  const handleNextKr = () => {
    form
      .validateFields()
      .then(values => {
        const payload = MapperPayload(values);
        setUpdating(true);
        dispatch(checkIntKrQuantYear(payload))
          .then(response => {
            const result = (response.payload as any).data
            if (!result) return;
            dispatch(reset());
            setUpdateSuccess(true);
            form.resetFields();
            form.scrollToField('reportScheduleName');
            handleNextData();
          })
          .catch(() => {
            return;
          })
          .finally(() => {
            setUpdating(false);
          });
      })
      .catch((err) => {
        return err;
      })
  };

  const handleEditKr = () => {
    setIsOpenModalFormKr(true);
  };

  const handleCloseModelKr = () => {
    setIsOpenModalFormKr(false);
  };

  const handleEditObjective = () => {
    setIsOpenModalFormObjective(true);
  };

  const handleCloseModelObjective = () => {
    setIsOpenModalFormObjective(false);
  };

  return (
    <Spin delay={500} spinning={isUpdating}>
      <Form size="large" form={form} className='fw-bold' disabled={isReadOnly} onFinish={handleSubmit} layout="vertical">
        <Title className="fw-bold" level={2}>
          {`Báo cáo thực hiện KR ${filters?.accountableName ? filters.accountableName : ''}`}
        </Title>
        <Row>
          <Col md={5}>
            <Form.Item label="Kỳ kế hoạch">
              <Input className="w-100" disabled value={entity?.planPeriodName} size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={5}>
            <Form.Item initialValue={entity?.reportScheduleName} label="Kỳ báo cáo" name={'reportScheduleName'}>
              <Input size='large' readOnly />
            </Form.Item>
          </Col>
          {/* <Col md={1} />
          <Col>
            <div className="mt-4">
              <span className="cursor-pointer text-primary" onClick={() => handleEditKr()}>
                {`Xem chi tiết KR >`}
              </span>
              <br />
              <span className="cursor-pointer text-primary" onClick={() => handleEditObjective()}>
                {`Xem chi tiết mục tiêu >`}
              </span>
            </div>
          </Col> */}
        </Row>
        <Row>
          <Col md={11}>
            <Form.Item label={`Mục tiêu - ${entity?.objectiveInfo?.code}`}>
              <TextArea rows={1} className="w-100" disabled value={entity?.objectiveInfo?.purpose} size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={3}>
            <Form.Item label="Lĩnh vực">
              <Input className="w-100" disabled value={entity?.objectiveInfo?.fieldName} size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={3}>
            <Form.Item label="Dự án">
              <Input className="w-100" disabled value={entity?.objectiveInfo?.projectName} size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={4}>
            <Form.Item label="Mảng công việc">
              <Input className="w-100" disabled value={entity?.objectiveInfo?.workTypeName} size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col md={3}>
            <Form.Item label="Mã">
              <Input className="w-100" disabled value={entity?.krYear?.code} size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={7}>
            <Form.Item label="Tên chỉ tiêu">
              <Input className="w-100" disabled value={entity?.krYear?.metricName} size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={5}>
            <Form.Item label="Đơn vị tính">
              <Input className="w-100" disabled value={entity?.krYear?.metricUnit} size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={6}>
            <Form.Item label="Chỉ tiêu năm">
              <InputNumber
                className="w-100"
                formatter={formatNumber}
                parser={parseNumber}
                disabled
                value={_.isInteger(entity?.krYear?.metricValue) ? entity?.krYear?.metricValue : entity?.krYear?.metricValue?.toFixed(2)}
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>
        <hr />
        <Row>
          <Col className="fw-bold h3" md={24}>
            Chốt kết quả thực hiện {entity?.krPrevMonth?.name}
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <Form.Item label={`Chỉ tiêu ${entity?.krPrevMonth?.name}`}>
              <InputNumber
                className="w-100"
                formatter={formatNumber}
                parser={parseNumber}
                disabled
                value={_.isInteger(entity?.krPrevMonth?.metricValue) ? entity?.krPrevMonth?.metricValue : entity?.krPrevMonth?.metricValue?.toFixed(2)}
                size="large"
              />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={4}>
            <Form.Item label={`Ước thực hiện ${entity?.krPrevMonth?.name}`}>
              <InputNumber
                formatter={formatNumber}
                parser={parseNumber}
                className="w-100"
                disabled
                value={_.isInteger(entity?.krPrevMonth?.result) ? entity?.krPrevMonth?.result : entity?.krPrevMonth?.result?.toFixed(2)}
                size="large"
              />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={4}>
            <Form.Item label={`Chốt thực hiện ${entity?.krPrevMonth?.name}`}
              rules={[{ required: true, message: `Hãy nhập chốt thực hiện ${entity?.krPrevMonth?.name}` }]}
              name={'actualResultMonth'}
            >
              <InputNumber step={0.01} precision={2} formatter={formatNumber} parser={parseNumber} className="w-100" size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
        </Row>
        <hr />
        <Row>
          <Col className="fw-bold h3" md={24}>
            {`Thực hiện ${entity?.krQuarter?.name} & ${entity?.krYear?.name}`}
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <Form.Item label={`Chỉ tiêu ${entity?.krQuarter?.name}`}>
              <InputNumber
                className="w-100"
                formatter={formatNumber}
                parser={parseNumber}
                disabled
                value={_.isInteger(entity?.krQuarter?.metricValue) ? entity?.krQuarter?.metricValue : entity?.krQuarter?.metricValue?.toFixed(2)}
                size="large"
              />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={4}>
            <Form.Item label={`Chỉ tiêu ${entity?.krMonth?.name}`}>
              <InputNumber
                formatter={formatNumber}
                parser={parseNumber}
                className="w-100"
                disabled
                value={_.isInteger(entity?.krMonth?.metricValue) ? entity?.krMonth?.metricValue : entity?.krMonth?.metricValue?.toFixed(2)}
                size="large"
              />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={4}>
            <Form.Item label={`Thực hiện ${entity?.krMonth?.name}`}
              rules={[{ required: true, message: `Hãy nhập thực ${entity?.krMonth?.name}` }]}
              name={'metricValueMonth'}
            >
              <InputNumber step={0.01} precision={2} formatter={formatNumber} parser={parseNumber} className="w-100" size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={4}>
            <Form.Item label={`Thực hiện ${entity?.krQuarter?.name}`} name={'metricValueQuarter'}>
              <InputNumber
                disabled={krCheckIn?.krType === KrTypes.AccumulateQuantitative}
                formatter={formatNumber}
                parser={parseNumber}
                className="w-100" size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={4}>
            <Form.Item label="Thực hiện năm" name={'metricValueYear'}>
              <InputNumber
                disabled={krCheckIn?.krType === KrTypes.AccumulateQuantitative}
                formatter={formatNumber}
                parser={parseNumber}
                className="w-100"
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col md={9}>
            <Form.Item label="Tỷ lệ hoàn thành (%)">
              <TextArea
                rows={1}
                disabled
                className="w-100"
                value={entity?.krYear?.evaluationMethodQuantitativeNote}
                size="large"
              />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={4}>
            <Form.Item label={`KR ${entity?.krMonth?.name}`} name={'estimatePointMonth'}>
              <InputNumber
                disabled={entity?.krMonth?.evaluationMethodQuantitative === QuantitativeEvaluationType.Point}
                formatter={formatNumber}
                parser={parseNumber}
                className="w-100"
                size="large"
              />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={4}>
            <Form.Item
              label={`KR ${entity?.krQuarter?.name}`} name={'estimatePointQuarter'}>
              <InputNumber
                disabled={entity?.krQuarter?.evaluationMethodQuantitative === QuantitativeEvaluationType.Point}
                formatter={formatNumber}
                parser={parseNumber}
                className="w-100"
                size="large"
              />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={4}>
            <Form.Item label={`KR ${entity?.krYear?.name}`} name={'estimatePointYear'}>
              <InputNumber
                disabled={entity?.krYear?.evaluationMethodQuantitative === QuantitativeEvaluationType.Point}
                formatter={formatNumber}
                parser={parseNumber}
                className="w-100"
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col md={7}>
            <Form.Item label={`Tình trạng KR ${entity?.krMonth?.name}`}
              rules={[{ required: true, message: 'Hãy chọn tình trạng' }]}
              name="workStatusMonth">
              <Select
                filterOption={filterOption}
                allowClear
                showSearch
                size="large"
                options={workStatusList}
                placeholder="Chọn tình trạng"
              />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={7}>
            <Form.Item label={`Tình trạng KR ${entity?.krQuarter?.name}`}
              rules={[{ required: true, message: 'Hãy chọn tình trạng' }]}
              name="workStatusQuarter">
              <Select
                filterOption={filterOption}
                allowClear
                showSearch
                size="large"
                options={workStatusList}
                placeholder="Chọn tình trạng"
              />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={8}>
            <Form.Item label={`Tình trạng KR năm`}
              rules={[{ required: true, message: 'Hãy chọn tình trạng' }]}
              name="workStatusYear">
              <Select
                filterOption={filterOption}
                allowClear
                showSearch
                size="large"
                options={workStatusList}
                placeholder="Chọn tình trạng"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col md={7}>
            <Form.Item
              label="Lý do (Không hoàn thành/Tạm dừng/Hủy)"
              rules={[
                {
                  required: workStatusMonthValues && (workStatusMonthValues === StatusType.Postponed || workStatusMonthValues === StatusType.Cancel || workStatusMonthValues === StatusType.NotCompleted),
                  message: `Hãy nhập lý do`,
                },
              ]}
              name={'unFinishNoteMonth'}
            >
              <TextArea rows={1} placeholder="Nhập lý do" size="large" />
            </Form.Item>

          </Col>
          <Col md={1} />
          <Col md={7}>
            <Form.Item
              label="Lý do (Không hoàn thành/Tạm dừng/Hủy)"
              rules={[
                {
                  required: workStatusQuarterValues && (workStatusQuarterValues === StatusType.Postponed || workStatusQuarterValues === StatusType.Cancel || workStatusQuarterValues === StatusType.NotCompleted),
                  message: `Hãy nhập lý do`,
                },
              ]}
              name={'unFinishNoteQuarter'}
            >
              <TextArea rows={1} placeholder="Nhập lý do" size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={8}>
            <Form.Item
              label="Lý do (Không hoàn thành/Tạm dừng/Hủy)"
              rules={[
                {
                  required: workStatusYearValues && (workStatusYearValues === StatusType.Postponed || workStatusYearValues === StatusType.Cancel || workStatusYearValues === StatusType.NotCompleted),
                  message: `Hãy nhập lý do`,
                },
              ]}
              name={'unFinishNoteYear'}
            >
              <TextArea rows={1} placeholder="Nhập lý do" size="large" />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col md={11}>
            <Form.Item label="Khó khăn & nguyên nhân" name={'issue'}>
              <TextArea rows={6} placeholder="Nhập khó khăn & nguyên nhân" size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={12}>
            <Form.Item label="Giải pháp và kiến nghị" name={'solution'}>
              <TextArea rows={6} placeholder="Nhập giải pháp và kiến nghị" size="large" />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col md={24}>
            <Form.Item label="Link tài liệu đính kèm" name={'note'}>
              <TextArea placeholder="Nhập tài liệu đính kèm về kết quả thực hiện" size="large" />
            </Form.Item>
            <Form.Item valuePropName="checked" name={'sendingNotification'}>
              <Checkbox>Gửi thông báo đến người nhận</Checkbox>
            </Form.Item>
          </Col>
        </Row>
        <Form.Item>
          <Row className="d-flex gap-3 mt-3" justify="end">
            {!isReadOnly &&
              <Col>
                <Button
                  className="green"
                  onClick={() => {
                    handleNextKr();
                  }}
                  disabled={isUpdating}
                >
                  Lưu & Chuyển sang check in KR khác
                </Button>
              </Col>
            }
            <Col>
              <Button
                className="secondary"
                onClick={() => {
                  onClose();
                }}
              >
                Hủy
              </Button>
            </Col>
            {!isReadOnly &&
              <Col>
                <Button className="primary" htmlType="submit" disabled={isUpdating}>
                  Lưu
                </Button>
              </Col>
            }
          </Row>
        </Form.Item>
        {isOpenModalFormObjective && (
          <ObjectiveUpdate isOpen={isOpenModalFormObjective} id={entity?.objective?.id} onClose={handleCloseModelObjective} />
        )}
        {isOpenModalFormKr && (
          <KRUpdate
            isOpen={isOpenModalFormKr}
            idKrBaseSelected={krCheckIn?.id}
            idObjective={entity?.objective?.id}
            planPeriodType={filters?.planPeriodType}
            krTypeRequest={krCheckIn?.krType}
            planPeriodIdSelected={filters?.planPeriodId}
            onClose={handleCloseModelKr}
          />
        )}
      </Form>
    </Spin>
  );
};

export default ReportQuantitativeResult;
